export interface GeocodingResult {
  latitude: number;
  longitude: number;
}

export interface AddressComponents {
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

/**
 * Build a full address string from components in Google-friendly format
 */
export const buildFullAddress = (components: AddressComponents): string => {
  const parts = [
    components.streetAddress,
    components.city,
    components.state,
    components.country,
    components.postalCode,
  ].filter(Boolean);

  return parts.join(', ');
};

// Wait for Google Maps to load
const waitForGoogleMaps = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (window.google && window.google.maps) {
      resolve(true);
      return;
    }

    const checkInterval = setInterval(() => {
      if (window.google && window.google.maps) {
        clearInterval(checkInterval);
        resolve(true);
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      resolve(false);
    }, 10000);
  });
};

/**
 * Geocode an address using Google Geocoding API (JavaScript API version)
 * @param address - Full address string to geocode
 * @returns GeocodingResult with lat/lng or null if not found
 */
export const geocodeAddress = async (
  address: string
): Promise<GeocodingResult | null> => {
  if (!address.trim()) {
    return null;
  }

  const isReady = await waitForGoogleMaps();
  if (!isReady || !window.google || !window.google.maps) {
    console.warn('Google Maps API is not loaded');
    return null;
  }

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
        const location = results[0].geometry.location;
        resolve({
          latitude: location.lat(),
          longitude: location.lng(),
        });
      } else {
        console.warn('Geocoding failed:', status);
        resolve(null);
      }
    });
  });
};

/**
 * Check if two addresses are the same (for optimization - avoid duplicate API calls)
 */
export const areAddressesSame = (
  address1: AddressComponents,
  address2: AddressComponents
): boolean => {
  return (
    address1.streetAddress.toLowerCase().trim() ===
      address2.streetAddress.toLowerCase().trim() &&
    address1.city.toLowerCase().trim() === address2.city.toLowerCase().trim() &&
    address1.state.toLowerCase().trim() === address2.state.toLowerCase().trim() &&
    address1.country.toLowerCase().trim() ===
      address2.country.toLowerCase().trim() &&
    address1.postalCode.toLowerCase().trim() ===
      address2.postalCode.toLowerCase().trim()
  );
};
